<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  NButton, NEmpty, NSpin, NResult, NCard, NSpace, NAvatar,
  NDivider, NTag, NGrid, NGridItem, NAlert, NIcon, NModal,
  NUpload, NUploadDragger, NPopover, NProgress, NTable, NCollapse,
  NCollapseItem, NTimeline, NTimelineItem, NTooltip, NInput, NCheckbox, useMessage
} from 'naive-ui';
import imageCompression from 'browser-image-compression';
import {
  fetchProviderProfile,
  uploadProviderProfileImage,
  getProviderProfileImage,
  getProviderImages,
  initiateProviderVerification,
  markVerificationSubmitted
} from '../services/provider';
import { authToken, isAuthenticated } from '../services/auth';
import {
  Camera,
  X as CloseIcon,
  Upload as UploadIcon,
  User,
  Edit as EditIcon,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Clock,
  Award,
  Briefcase,
  DollarSign,
  MessageSquare,
  Globe,
  Heart,
  Check,
  Video,
  Accessibility,
  Car,
  CreditCard,
  Languages as LanguagesIcon,
  School,
  Image as ImageIcon,
  FileText,
  Link,
  Shield,
  AlertTriangle,
  ExternalLink
} from 'lucide-vue-next';

// State variables
const provider = ref(null);
const loading = ref(true);
const error = ref(null);
const router = useRouter();
const message = useMessage();

// Profile image state variables
const profileImageUrl = ref(null);
const isUploadingImage = ref(false);
const imageUploadError = ref(null);
const showImageModal = ref(false);

// Provider images state
const facilityImages = ref([]);
const loadingImages = ref(false);

// Verification state
const initiatingVerification = ref(false);
const submittingVerification = ref(false);
const clockclearUrl = ref(''); // NEW: Store the ClockClear share link the user pastes
const verificationUrl = ref(''); // NEW: Store the verification URL to show to user
const confirmDocumentsOwnership = ref(false); // NEW: Checkbox for confirming document ownership

// Load provider profile when component mounts
onMounted(async () => {
  if (!isAuthenticated.value) {
    error.value = 'Please log in to view your provider profile';
    loading.value = false;
    return;
  }

  await loadProviderProfile();

  // Check if there's a pending verification session in localStorage
  try {
    const savedSession = localStorage.getItem('verification_session');
    if (savedSession && savedSession.trim() !== '') {
      try {
        const session = JSON.parse(savedSession);
        // Check if session is still valid (not expired)
        if (session.expires_at && session.verification_url) {
          const expiresAt = new Date(session.expires_at);
          if (expiresAt > new Date()) {
            verificationUrl.value = session.verification_url;
            console.log('Restored verification session from localStorage');
          } else {
            // Session expired, clear it
            console.log('Session expired, clearing localStorage');
            localStorage.removeItem('verification_session');
          }
        } else {
          // Invalid session data, clear it
          console.log('Invalid session data, clearing localStorage');
          localStorage.removeItem('verification_session');
        }
      } catch (parseError) {
        console.error('Error parsing saved session:', parseError);
        localStorage.removeItem('verification_session');
      }
    }
  } catch (storageError) {
    console.error('Error accessing localStorage:', storageError);
  }
});

const verificationStatus = computed(() => {
  // If there's a verification URL (session active), we're in the input stage
  if (verificationUrl.value) {
    return 'awaiting_submission';
  }

  // Check database status first - this was missing!
  if (provider.value?.status === 'verified') {
    return 'verified';
  }

  if (provider.value?.status === 'pending') {
    return 'pending'; // or 'in_progress' 
  }

  // If status is 'unverified' but has verification_info, they submitted docs
  if (provider.value?.status === 'unverified' && provider.value?.verification_info?.submitted_at) {
      return 'in_progress';
  }

  // Default unverified
  return 'unverified';
});

// Function to load provider profile
async function loadProviderProfile() {
  try {
    loading.value = true;
    error.value = null;

    const data = await fetchProviderProfile(authToken.value);
    provider.value = data;

    console.log("provider data => ", data);

    // Fetch profile image if we have a provider ID
    if (data && data.id) {
      try {
        const imageResult = await getProviderProfileImage(data.id);
        if (imageResult.success && imageResult.image_url) {
          profileImageUrl.value = imageResult.image_url;
        }

        // Load facility images
        await loadFacilityImages(data.id);
      } catch (imageError) {
        console.error('Error loading profile image:', imageError);
        // Don't set main error, just log it since the profile loaded successfully
      }
    }
  } catch (err) {
    console.error('Failed to load provider profile:', err);
    error.value = 'Failed to load provider profile. Please try again later.';
  } finally {
    loading.value = false;
  }
}

// Load facility images
async function loadFacilityImages(providerId) {
  try {
    loadingImages.value = true;
    const result = await getProviderImages(providerId);

    if (result.success && result.images) {
      facilityImages.value = result.images;
    }
  } catch (err) {
    console.error('Failed to load facility images:', err);
  } finally {
    loadingImages.value = false;
  }
}

// Verification functions
async function startVerification() {
  if (!canStartVerification()) {
    message.warning('Please complete your profile before starting verification');
    return;
  }

  try {
    initiatingVerification.value = true;

    const result = await initiateProviderVerification(authToken.value, provider.value.id, {
      provider_id: provider.value.id
    });

    console.log('Verification result:', result); // Debug log

    if (result.success && result.verification) {
      // Extract the verification data from the nested object
      const verificationData = result.verification;

      // Save session data to localStorage
      const sessionData = {
        session_id: verificationData.session_id,
        signature_hash: verificationData.signature_hash,
        verification_url: verificationData.verification_url,
        expires_at: verificationData.expires_at,
        provider_id: provider.value.id
      };

      localStorage.setItem('verification_session', JSON.stringify(sessionData));

      // Set the verification URL - this will trigger the card to show
      verificationUrl.value = verificationData.verification_url;

      console.log('verificationUrl set to:', verificationUrl.value); // Debug log

      message.success('Verification session created! The verification card is now available below.');

      // Scroll down to the verification card after a brief delay
      setTimeout(() => {
        const verificationCard = document.querySelector('.verification-card');
        if (verificationCard) {
          verificationCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

    } else {
      message.error(result.error || 'Failed to initiate verification');
    }
  } catch (error) {
    console.error('Error initiating verification:', error);
    message.error('Failed to start verification process');
  } finally {
    initiatingVerification.value = false;
  }
}

async function submitVerification() {
  // Validate that user has pasted a ClockClear URL
  if (!clockclearUrl.value || !clockclearUrl.value.trim()) {
    message.warning('Please paste the ClockClear verification link you received after uploading documents');
    return;
  }

  // Validate URL format (basic check)
  if (!clockclearUrl.value.includes('clockclear.com')) {
    message.warning('Please enter a valid ClockClear verification link');
    return;
  }

  // NEW: Validate that user confirmed ownership
  if (!confirmDocumentsOwnership.value) {
    message.warning('Please confirm that the documents you uploaded are yours and accurate');
    return;
  }

  try {
    submittingVerification.value = true;

    // Get the session data from localStorage
    const savedSession = localStorage.getItem('verification_session');
    if (!savedSession || savedSession.trim() === '') {
      message.error('Verification session not found. Please start the verification process again.');
      return;
    }

    let session;
    try {
      session = JSON.parse(savedSession);
    } catch (parseError) {
      console.error('Error parsing session:', parseError);
      message.error('Invalid session data. Please start the verification process again.');
      localStorage.removeItem('verification_session');
      verificationUrl.value = '';
      return;
    }

    // Validate session data
    if (!session.session_id || !session.signature_hash) {
      message.error('Incomplete session data. Please start the verification process again.');
      localStorage.removeItem('verification_session');
      verificationUrl.value = '';
      return;
    }

    // Check if session expired
    const expiresAt = new Date(session.expires_at);
    if (expiresAt < new Date()) {
      message.error('Verification session has expired. Please start a new verification process.');
      localStorage.removeItem('verification_session');
      verificationUrl.value = '';
      return;
    }

    // Send the correct data with session_id, clockclear_url, and signature
    const result = await markVerificationSubmitted(authToken.value, {
      session_id: session.session_id,
      clockclear_url: clockclearUrl.value.trim(),
      digital_signature_hash: session.signature_hash
    });

    if (result.success) {
      message.success('Verification submitted successfully! Our team will review your documents.');

      // Clear the form and localStorage
      clockclearUrl.value = '';
      verificationUrl.value = '';
      confirmDocumentsOwnership.value = false; // NEW: Reset checkbox
      localStorage.removeItem('verification_session');

      // Reload the profile to get updated verification status
      await loadProviderProfile();
    } else {
      message.error(result.error || 'Failed to submit verification');
    }
  } catch (error) {
    console.error('Error submitting verification:', error);
    message.error('Failed to submit verification');
  } finally {
    submittingVerification.value = false;
  }
}

// Helper functions for verification
function canStartVerification() {
  return provider.value?.provider_name &&
    provider.value?.specializations?.length > 0 &&
    provider.value?.years_of_experience &&
    profileImageUrl.value;
}

function getVerificationStatusType(status) {
  switch (status) {
    case 'verified': return 'success';
    case 'pending': return 'warning';
    case 'in_progress': return 'info';
    case 'awaiting_submission': return 'info';
    case 'rejected': return 'error';
    default: return 'default';
  }
}

function getVerificationAlertType(status) {
  switch (status) {
    case 'pending': return 'warning';
    case 'in_progress': return 'info';
    case 'awaiting_submission': return 'info';
    case 'rejected': return 'error';
    default: return 'info';
  }
}

function formatVerificationStatus(status) {
  switch (status) {
    case 'verified': return 'Verified Provider';
    case 'pending': return 'Pending Review';
    case 'in_progress': return 'In Progress';
    case 'awaiting_submission': return 'Upload Documents';
    case 'rejected': return 'Verification Failed';
    case 'unverified': return 'Not Verified';
    default: return 'Unknown Status';
  }
}

function getVerificationTitle(status) {
  switch (status) {
    case 'pending': return 'Verification Under Review';
    case 'in_progress': return 'Awaiting Admin Review';
    case 'awaiting_submission': return 'Upload Your Documents';
    case 'rejected': return 'Verification Rejected';
    default: return 'Start Verification Process';
  }
}

function getVerificationMessage(status) {
  switch (status) {
    case 'pending': return 'Your verification documents are being reviewed by our team. This typically takes 2-3 business days.';
    case 'in_progress': return 'Your documents have been submitted and are awaiting admin review. This typically takes 2-3 business days.';
    case 'awaiting_submission': return 'Click the button below to upload your documents to ClockClear, then paste the share link to submit for review.';
    case 'rejected': return 'Your verification was rejected. Please check the feedback and resubmit with corrected information.';
    default: return 'Complete your profile and start the verification process to become a verified provider.';
  }
}

// Navigation functions - preserved from the original working code
function navigateToCreate() {
  if (provider.value && provider.value.status !== 'not_found') {
    if (provider.value.id) {
      router.push(`/provider/manage/${provider.value.id}`)
    }
    return  // ← don't proceed to create either way
  }
  router.push('/provider/manage')
}

function navigateToManage() {
  if (provider.value && provider.value.id) {
    router.push(`/provider/manage/${provider.value.id}`);
  } else {
    router.push('/provider/manage');
  }
}

function navigateToFacilityImages() {
  if (provider.value && provider.value.id) {
    router.push(`/provider/images/${provider.value.id}`);
  } else {
    error.value = "You must create a provider profile before managing images";
  }
}

// Helper function to get initials from name
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Format office hours for display
function formatOfficeHours(officeHours) {
  if (!officeHours || !officeHours.length) return [];

  // Sort by day of week
  const dayOrder = {
    'Monday': 0,
    'Tuesday': 1,
    'Wednesday': 2,
    'Thursday': 3,
    'Friday': 4,
    'Saturday': 5,
    'Sunday': 6
  };

  return [...officeHours].sort((a, b) => {
    return dayOrder[a.day] - dayOrder[b.day];
  });
}

// Calculate profile completeness - preserved the old logic but expanded
function calculateProfileCompleteness() {
  if (!provider.value) return 0;

  // Use existing completeness if available
  if (provider.value.profile_completeness !== undefined) {
    return provider.value.profile_completeness;
  }

  // Or calculate based on filled fields - keeping the original fields and adding new ones
  const requiredFields = [
    'provider_name',
    'specializations',
    'phone_numbers',
    'address_line1',
    'city',
    'additional_info',
    'insurance_accepted',
    'tags'
  ];

  let completedCount = 0;
  requiredFields.forEach(field => {
    if (Array.isArray(provider.value[field])) {
      if (provider.value[field] && provider.value[field].length > 0) completedCount++;
    } else if (provider.value[field]) {
      completedCount++;
    }
  });

  // Add bonus for additional fields
  if (profileImageUrl.value) {
    completedCount += 1;
  }

  if (facilityImages.value && facilityImages.value.length > 0) {
    completedCount += 1;
  }

  return Math.round((completedCount / (requiredFields.length + 2)) * 100);
}

const isEditMode = computed(() => {
  return provider.value?.status !== 'not_found';
});


// Handle image upload - preserved from the original working code
const handleImageUpload = async (options) => {
  const { file } = options;

  try {
    // Check if provider exists
    if (!provider.value || !provider.value.id) {
      imageUploadError.value = "You must create a provider profile before uploading an image";
      options.onError();
      return;
    }

    isUploadingImage.value = true;
    imageUploadError.value = null;

    console.log('[Upload] Original file:', file.file.name, 'Size:', file.file.size, 'bytes');

    // Check file size before compression
    if (file.file.size > 20 * 1024 * 1024) { // 20MB limit
      imageUploadError.value = "File too large. Please select a file smaller than 20MB.";
      options.onError();
      return;
    }

    // Compression options
    const compressionOptions = {
      maxSizeMB: 2,           // Max 2MB after compression
      maxWidthOrHeight: 1200, // Match your backend resize
      useWebWorker: true,
      fileType: 'image/webp', // Convert to WebP for better compression
      quality: 0.8            // 80% quality
    };

    // Compress the image
    const compressedFile = await imageCompression(file.file, compressionOptions);
    console.log('[Compression] Compressed file size:', compressedFile.size, 'bytes');
    console.log('[Compression] Size reduction:',
      Math.round((1 - compressedFile.size / file.file.size) * 100) + '%');

    // Create a new File object with original name but .webp extension
    const webpFileName = file.file.name.replace(/\.[^/.]+$/, '.webp');
    const finalFile = new File([compressedFile], webpFileName, {
      type: 'image/webp'
    });

    // Upload the compressed image
    const result = await uploadProviderProfileImage(authToken.value, finalFile);

    // Update profile image URL
    if (result && result.image_url) {
      profileImageUrl.value = result.image_url;
      options.onFinish();

      // Close modal after successful upload
      showImageModal.value = false;

      console.log('[Upload] Success! Image uploaded and URL updated');
    } else {
      imageUploadError.value = result.error || "Failed to upload image";
      options.onError();
    }
  } catch (err) {
    console.error('[Upload] Error:', err);

    // Better error messages
    if (err.message.includes('body limit') || err.message.includes('Payload Too Large')) {
      imageUploadError.value = "File too large. Please try compressing your image further.";
    } else if (err.message.includes('compression')) {
      imageUploadError.value = "Failed to compress image. Please try a different image.";
    } else {
      imageUploadError.value = err.message || "Failed to upload image";
    }

    options.onError();
  } finally {
    isUploadingImage.value = false;
  }
};

// Open image modal - only if provider exists
const openImageModal = () => {
  if (!provider.value || !provider.value.id) {
    error.value = "You must create a provider profile before uploading an image";
    return;
  }

  showImageModal.value = true;
  imageUploadError.value = null;
};

// Close image upload modal
const closeImageModal = () => {
  showImageModal.value = false;
  imageUploadError.value = null;
};

// View larger profile image
const viewLargerImage = () => {
  if (profileImageUrl.value) {
    window.open(profileImageUrl.value, '_blank');
  }
};

// Create view status text and color
const statusInfo = computed(() => {
  if (!provider.value) return { text: 'Not Created', color: 'error' };

  switch (provider.value.status) {
    case 'draft':
      return { text: 'Draft (Not Visible)', color: 'warning' };
    case 'unverified':
      return { text: 'Unverified', color: 'info' };
    case 'verified':
      return { text: 'Verified', color: 'success' };
    case 'suspended':
      return { text: 'Suspended', color: 'error' };
    case 'claimed':
      return { text: 'Claimed', color: 'success' };
    case 'unclaimed':
      return { text: 'Unclaimed', color: 'warning' };
    default:
      return { text: 'Unknown', color: 'default' };
  }
});
</script>

<template>
  <div class="provider-dashboard-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <NSpin size="large" />
      <p>Loading your profile...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <NAlert type="error" :title="error" />
      <NButton class="retry-button" @click="loadProviderProfile">Try Again</NButton>
    </div>

    <div v-else>
      <!-- Profile Header Card -->
      <NCard class="profile-header">
        <div class="profile-header-content">
          <div class="profile-avatar-container">
            <NPopover trigger="hover" placement="bottom" v-if="provider && provider.id">
              <template #trigger>
                <NAvatar round size="large" :src="profileImageUrl" class="profile-avatar" @click="viewLargerImage">
                  <template v-if="!profileImageUrl">
                    {{ getInitials(provider.provider_name) }}
                  </template>
                </NAvatar>
              </template>
              <div class="avatar-popover">
                <p>{{ profileImageUrl ? 'Click to view full image' : 'No profile image' }}</p>
                <NButton size="small" @click="openImageModal" type="primary" round>
                  <template #icon>
                    <NIcon>
                      <Camera />
                    </NIcon>
                  </template>
                  {{ profileImageUrl ? 'Change Image' : 'Add Image' }}
                </NButton>
              </div>
            </NPopover>

            <!-- If no provider profile exists, just show the avatar without the popover -->
            <NAvatar v-else round size="large" class="profile-avatar">
              ?
            </NAvatar>

            <!-- Camera button for profile image - only if provider exists -->
            <NButton v-if="provider && provider.id" class="camera-button" circle type="primary" @click="openImageModal"
              size="small">
              <NIcon>
                <Camera />
              </NIcon>
            </NButton>
          </div>

          <div class="profile-info">
            <h2>{{ provider?.provider_name || 'Your Provider Profile' }}</h2>
            <div v-if="provider && provider.provider_type" class="provider-type-display">
              {{ provider.provider_type }}
              <NTag :type="statusInfo.color" size="small">{{ statusInfo.text }}</NTag>

              <!-- Verification Status Tag -->
              <NTag v-if="verificationStatus !== 'unverified'" :type="getVerificationStatusType(verificationStatus)"
                size="small">
                <template #icon>
                  <NIcon>
                    <Shield v-if="verificationStatus === 'verified'" />
                    <Clock v-else-if="verificationStatus === 'pending'" />
                    <AlertTriangle v-else />
                  </NIcon>
                </template>
                {{ formatVerificationStatus(verificationStatus) }}
              </NTag>
            </div>

            <div v-if="provider" class="profile-completeness">
              <div class="completeness-header">
                <span>Profile Completeness</span>
                <NTag :type="calculateProfileCompleteness() >= 70 ? 'success' : 'warning'" round>
                  {{ calculateProfileCompleteness() }}%
                </NTag>
              </div>
              <NProgress type="line" :percentage="calculateProfileCompleteness()"
                :processing="calculateProfileCompleteness() < 100"
                :status="calculateProfileCompleteness() >= 70 ? 'success' : 'warning'" :height="8" />
            </div>
          </div>

          <div class="profile-actions">
            <NSpace vertical size="small">
              <NButton v-if="isEditMode" type="primary" @click="navigateToManage" round>
                <template #icon>
                  <NIcon>
                    <EditIcon />
                  </NIcon>
                </template>
                Update Profile
              </NButton>
              <NButton v-else type="primary" @click="navigateToCreate" round>
                <template #icon>
                  <NIcon>
                    <User />
                  </NIcon>
                </template>
                Create Profile
              </NButton>

              <!-- Verification Action Buttons -->
              <NButton v-if="provider && verificationStatus === 'unverified'" type="success" @click="startVerification"
                :loading="initiatingVerification" :disabled="!canStartVerification()" round>
                <template #icon>
                  <NIcon>
                    <Shield />
                  </NIcon>
                </template>
                Start Verification
              </NButton>

              <NButton v-else-if="provider && verificationStatus === 'awaiting_submission'" type="warning"
                @click="submitVerification" :loading="submittingVerification" :disabled="!clockclearUrl.trim()" round>
                <template #icon>
                  <NIcon>
                    <Check />
                  </NIcon>
                </template>
                Submit for Review
              </NButton>

              <NTag v-else-if="provider && verificationStatus === 'in_progress'" type="info" size="large" round>
                <template #icon>
                  <NIcon>
                    <Clock />
                  </NIcon>
                </template>
                Under Review
              </NTag>

              <NTag v-else-if="provider && verificationStatus === 'pending'" type="warning" size="large" round>
                <template #icon>
                  <NIcon>
                    <Clock />
                  </NIcon>
                </template>
                Pending Review
              </NTag>
            </NSpace>
          </div>
        </div>
      </NCard>

      <!-- Verification Status Card -->
      <NCard
        v-if="provider && (verificationStatus === 'awaiting_submission' || verificationStatus === 'in_progress' || verificationStatus === 'pending')"
        title="Verification Status" class="profile-section verification-card">
        <template #header-extra>
          <NIcon>
            <Shield />
          </NIcon>
        </template>

        <div class="verification-content">
          <NAlert :type="getVerificationAlertType(verificationStatus)" :show-icon="true">
            <template #header>
              {{ getVerificationTitle(verificationStatus) }}
            </template>
            {{ getVerificationMessage(verificationStatus) }}
          </NAlert>

          <!-- Show verification URL if status is awaiting_submission -->
          <div v-if="verificationStatus === 'awaiting_submission'" class="verification-link-section">
            <h3>Step 1: Upload Your Documents</h3>
            <p class="step-description">Click the button below to open ClockClear and upload your verification documents
              (medical license, ID, certificates, etc.)</p>
            <NButton type="primary" tag="a" :href="verificationUrl" target="_blank" size="large">
              <template #icon>
                <NIcon>
                  <ExternalLink />
                </NIcon>
              </template>
              Open ClockClear to Upload Documents
            </NButton>
            <p class="link-hint">This link will expire in 24 hours</p>
          </div>

          <!-- Show input field for ClockClear share link if status is awaiting_submission -->
          <div v-if="verificationStatus === 'awaiting_submission'" class="verification-submit-section">
            <NDivider />
            <h3>Step 2: Submit Your ClockClear Link</h3>
            <p class="step-description">After uploading your documents to ClockClear, you'll receive a share link. Paste
              it
              below:</p>

            <div class="clockclear-input-container">
              <NInput v-model:value="clockclearUrl" type="text"
                placeholder="Paste your ClockClear verification link here (e.g., https://in.clockclear.com/verify?id=...)"
                size="large" clearable>
                <template #prefix>
                  <NIcon>
                    <Link />
                  </NIcon>
                </template>
              </NInput>
            </div>

            <div class="confirmation-checkbox">
              <NCheckbox v-model:checked="confirmDocumentsOwnership" size="large">
                I confirm that the documents I uploaded are mine and the information is accurate
              </NCheckbox>
            </div>

            <div class="submit-button-container">
              <NButton type="success" @click="submitVerification" :loading="submittingVerification"
                :disabled="!clockclearUrl.trim() || !confirmDocumentsOwnership" size="large">
                <template #icon>
                  <NIcon>
                    <Check />
                  </NIcon>
                </template>
                Submit for Admin Review
              </NButton>
            </div>

            <NAlert type="info" class="info-alert">
              <template #header>
                How It Works - Maximum Privacy
              </template>
              <ul>
                <li><strong>Encrypted in your browser:</strong> Your documents are encrypted on your device before
                  uploading
                </li>
                <li>ClockClear generates a secure verification link containing the decryption key</li>
                <li>Paste that verification link above and click "Submit for Admin Review"</li>
                <li>Our admin team will review your documents within 2-3 business days</li>
                <li><strong>Zero storage:</strong> Documents are never saved to disk - only held temporarily in memory
                  during
                  viewing</li>
                <li><strong>Instant deletion:</strong> Once the admin closes the verification link, your documents are
                  immediately removed from memory</li>
                <li>Complete privacy - no permanent copies exist anywhere</li>
              </ul>
            </NAlert>
          </div>

          <!-- Verification Checklist (only show if not started yet) -->
          <div v-if="verificationStatus === 'unverified'" class="verification-checklist">
            <h3>Verification Requirements</h3>
            <div class="checklist-items">
              <div class="checklist-item">
                <NIcon :color="provider.provider_name ? '#18a058' : '#d03050'">
                  <Check v-if="provider.provider_name" />
                  <CloseIcon v-else />
                </NIcon>
                <span>Provider Name</span>
              </div>

              <div class="checklist-item">
                <NIcon :color="provider.specializations?.length ? '#18a058' : '#d03050'">
                  <Check v-if="provider.specializations?.length" />
                  <CloseIcon v-else />
                </NIcon>
                <span>Medical Specializations</span>
              </div>

              <div class="checklist-item">
                <NIcon :color="provider.years_of_experience ? '#18a058' : '#d03050'">
                  <Check v-if="provider.years_of_experience" />
                  <CloseIcon v-else />
                </NIcon>
                <span>Years of Experience</span>
              </div>

              <div class="checklist-item">
                <NIcon :color="profileImageUrl ? '#18a058' : '#d03050'">
                  <Check v-if="profileImageUrl" />
                  <CloseIcon v-else />
                </NIcon>
                <span>Profile Photo</span>
              </div>
            </div>
          </div>

          <!-- Verification Actions (only show start button if unverified) -->
          <div v-if="verificationStatus === 'unverified'" class="verification-actions">
            <NButton type="primary" @click="startVerification" :loading="initiatingVerification"
              :disabled="!canStartVerification()" size="large">
              <template #icon>
                <NIcon>
                  <Shield />
                </NIcon>
              </template>
              Start Verification Process
            </NButton>

            <NButton quaternary @click="navigateToManage">
              <template #icon>
                <NIcon>
                  <EditIcon />
                </NIcon>
              </template>
              Complete Profile First
            </NButton>
          </div>
        </div>
      </NCard>

      <!-- Empty state -->
      <NCard v-if="!provider" class="profile-details">
        <NEmpty description="You don't have a provider profile yet.">
          <template #extra>
            <p>You haven't created your provider profile yet. Create your profile to be discoverable by patients.</p>
            <NButton type="primary" @click="navigateToCreate">Create Profile</NButton>
          </template>
        </NEmpty>
      </NCard>

      <!-- Provider profile content if exists -->
      <template v-else>
        <!-- Personal Info Section -->
        <NCard title="Personal Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <User />
            </NIcon>
          </template>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Provider Name</div>
              <div class="info-value">{{ provider.provider_name || 'Not provided' }}</div>
            </div>

            <div class="info-item" v-if="provider.provider_type">
              <div class="info-label">Provider Type</div>
              <div class="info-value">{{ provider.provider_type }}</div>
            </div>

            <div class="info-item" v-if="provider.years_of_experience">
              <div class="info-label">Years of Experience</div>
              <div class="info-value">{{ provider.years_of_experience }}</div>
            </div>

            <div class="info-item" v-if="provider.phone_numbers && provider.phone_numbers.length > 0">
              <div class="info-label">
                <NIcon size="small">
                  <Phone />
                </NIcon>
                Phone
              </div>
              <div class="info-value phone-list">
                <div v-for="(phone, index) in provider.phone_numbers" :key="index" class="phone-number">
                  {{ phone }}
                </div>
              </div>
            </div>

            <div class="info-item" v-if="provider.website_url">
              <div class="info-label">
                <NIcon size="small">
                  <Globe />
                </NIcon>
                Website
              </div>
              <div class="info-value">
                <a :href="provider.website_url" target="_blank" rel="noopener">{{ provider.website_url }}</a>
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <MapPin />
                </NIcon>
                Location
              </div>
              <div class="info-value">
                <div v-if="provider.city || provider.state">
                  {{ provider.city }}{{ provider.city && provider.state ? ', ' : '' }}{{ provider.state }}
                  {{ provider.postal_code ? provider.postal_code : '' }}
                </div>
                <div v-else>Not provided</div>
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">Address</div>
              <div class="info-value">
                <div v-if="provider.address_line1">{{ provider.address_line1 }}</div>
                <div v-if="provider.address_line2">{{ provider.address_line2 }}</div>
                <div v-if="provider.address_line3">{{ provider.address_line3 }}</div>
                <div v-if="!provider.address_line1 && !provider.address_line2 && !provider.address_line3">Not provided
                </div>
              </div>
            </div>

            <div class="info-item" v-if="provider.social_media && provider.social_media.length > 0">
              <div class="info-label">
                <NIcon size="small">
                  <Link />
                </NIcon>
                Social Media
              </div>
              <div class="info-value social-links">
                <div v-for="(social, index) in provider.social_media" :key="index" class="social-item">
                  <strong>{{ social.platform }}:</strong>
                  <a :href="social.url.startsWith('http') ? social.url : 'https://' + social.url" target="_blank"
                    rel="noopener">{{ social.url }}</a>
                </div>
              </div>
            </div>
          </div>

          <div v-if="provider.additional_info" class="bio-section">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <MessageSquare />
              </NIcon>
              About
            </h3>
            <div class="notes-box">{{ provider.additional_info }}</div>
          </div>
        </NCard>

        <!-- Location Information -->
        <NCard v-if="provider.additional_locations && provider.additional_locations.length > 0"
          title="Additional Locations" class="profile-section">
          <template #header-extra>
            <NIcon>
              <MapPin />
            </NIcon>
          </template>

          <div class="locations-grid">
            <div v-for="(location, index) in provider.additional_locations" :key="index" class="location-item">
              <div class="location-address">
                <div>{{ location.address }}</div>
                <div>{{ location.city }}, {{ location.state }} {{ location.postal_code }}</div>
              </div>
            </div>
          </div>
        </NCard>

        <!-- Professional Information -->
        <NCard title="Professional Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Briefcase />
            </NIcon>
          </template>

          <div v-if="provider.specializations && provider.specializations.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Award />
              </NIcon>
              Specializations
            </h3>

            <NSpace>
              <NTag v-for="(spec, index) in provider.specializations" :key="index" type="info" round size="large"
                class="specialty-tag">
                {{ spec }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No specializations provided</p>

          <NDivider />

          <div v-if="provider.procedures && provider.procedures.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Briefcase />
              </NIcon>
              Procedures Offered
            </h3>

            <NSpace>
              <NTag v-for="(procedure, index) in provider.procedures" :key="index" type="primary" round size="medium"
                class="procedure-tag">
                {{ procedure }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No procedures listed</p>

          <NDivider />

          <div v-if="provider.insurance_accepted && provider.insurance_accepted.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <DollarSign />
              </NIcon>
              Insurance Accepted
            </h3>

            <NSpace>
              <NTag v-for="(insurance, index) in provider.insurance_accepted" :key="index" type="warning" round
                size="medium" class="insurance-tag">
                {{ insurance }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No insurance information provided</p>

          <NDivider />

          <div v-if="provider.spoken_languages && provider.spoken_languages.length > 0">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <LanguagesIcon />
              </NIcon>
              Spoken Languages
            </h3>

            <NSpace>
              <NTag v-for="(language, index) in provider.spoken_languages" :key="index" type="default" round
                size="medium" class="language-tag">
                {{ language }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No language information provided</p>

          <div v-if="provider.education && provider.education.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <School />
              </NIcon>
              Education
            </h3>

            <NTimeline>
              <NTimelineItem v-for="(edu, index) in provider.education" :key="index" :title="edu.degree"
                :content="edu.institution" :time="edu.year ? edu.year.toString() : ''" />
            </NTimeline>
          </div>

          <div v-if="provider.certifications && provider.certifications.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <FileText />
              </NIcon>
              Certifications
            </h3>

            <NTimeline>
              <NTimelineItem v-for="(cert, index) in provider.certifications" :key="index" :title="cert.name"
                :content="cert.issuer" :time="cert.year ? cert.year.toString() : ''" />
            </NTimeline>
          </div>

          <div v-if="provider.tags && provider.tags.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <MessageSquare />
              </NIcon>
              Tags
            </h3>

            <NSpace>
              <NTag v-for="(tag, index) in provider.tags" :key="index" type="success" round size="medium"
                class="tag-item">
                {{ tag }}
              </NTag>
            </NSpace>
          </div>
        </NCard>

        <!-- Practice Information -->
        <NCard v-if="provider.accepts_new_patients !== null ||
          provider.telehealth_available !== null ||
          (provider.office_hours && provider.office_hours.length > 0) ||
          provider.parking_info ||
          (provider.payment_methods && provider.payment_methods.length > 0) ||
          (provider.accessibility_options && provider.accessibility_options.length > 0) ||
          provider.average_wait_time_minutes !== null" title="Practice Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Clock />
            </NIcon>
          </template>

          <div class="info-grid">
            <div v-if="provider.accepts_new_patients !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <Heart />
                </NIcon>
                Accepting New Patients
              </div>
              <div class="info-value" :class="{
                'status-positive': provider.accepts_new_patients,
                'status-negative': !provider.accepts_new_patients
              }">
                {{ provider.accepts_new_patients ? 'Yes' : 'No' }}
              </div>
            </div>

            <div v-if="provider.telehealth_available !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small"><Video /></NIcon>
                Telehealth Available
              </div>
              <div class="info-value" :class="{
                'status-positive': provider.telehealth_available,
                'status-negative': !provider.telehealth_available
              }">
                {{ provider.telehealth_available ? 'Yes' : 'No' }}
              </div>
            </div>

            <div v-if="provider.average_wait_time_minutes !== null" class="info-item">
              <div class="info-label">
                <NIcon size="small">
                  <Clock />
                </NIcon>
                Average Wait Time
              </div>
              <div class="info-value">
                {{ provider.average_wait_time_minutes }} minutes
              </div>
            </div>
          </div>

          <div v-if="provider.office_hours && provider.office_hours.length > 0">
            <h3 class="section-title" style="margin-top: 24px;">
              <NIcon class="section-icon">
                <Clock />
              </NIcon>
              Office Hours
            </h3>

            <NTable :bordered="false" :single-line="false">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(hours, index) in formatOfficeHours(provider.office_hours)" :key="index">
                  <td>{{ hours.day }}</td>
                  <td>{{ hours.open }} - {{ hours.close }}</td>
                </tr>
              </tbody>
            </NTable>
          </div>

          <div v-if="provider.parking_info" class="parking-info" style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Car />
              </NIcon>
              Parking Information
            </h3>
            <div class="notes-box">{{ provider.parking_info }}</div>
          </div>

          <div v-if="provider.payment_methods && provider.payment_methods.length > 0" style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <CreditCard />
              </NIcon>
              Payment Methods
            </h3>

            <NSpace>
              <NTag v-for="(method, index) in provider.payment_methods" :key="index" type="info" round size="medium"
                class="payment-tag">
                {{ method }}
              </NTag>
            </NSpace>
          </div>

          <div v-if="provider.accessibility_options && provider.accessibility_options.length > 0"
            style="margin-top: 24px;">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <Accessibility />
              </NIcon>
              Accessibility Features
            </h3>

            <NSpace>
              <NTag v-for="(option, index) in provider.accessibility_options" :key="index" type="success" round
                size="medium" class="accessibility-tag">
                {{ option }}
              </NTag>
            </NSpace>
          </div>
        </NCard>

        <!-- Facility Images -->
        <NCard v-if="facilityImages && facilityImages.length > 0" title="Facility Images" class="profile-section">
          <template #header-extra>
            <NIcon>
              <ImageIcon />
            </NIcon>
          </template>

          <div class="facility-images-grid">
            <div v-for="(image, index) in facilityImages" :key="image.id" class="facility-image-card"
              @click="() => window.open(image.presigned_url, '_blank')">
              <img :src="image.presigned_url" :alt="`Facility image ${index + 1}`" />
              <div class="image-overlay">
                <div class="image-type">{{ image.image_type || 'Facility' }}</div>
                <div class="image-description" v-if="image.description">{{ image.description }}</div>
              </div>
            </div>
          </div>

          <div class="manage-images-button">
            <NButton @click="navigateToFacilityImages">
              <template #icon>
                <NIcon>
                  <ImageIcon />
                </NIcon>
              </template>
              Manage Facility Images
            </NButton>
          </div>
        </NCard>

        <!-- Statistics Section -->
        <NCard title="Profile Statistics" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Calendar />
            </NIcon>
          </template>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Total Views</div>
              <div class="info-value stat-value">{{ provider.views || 0 }}</div>
            </div>

            <div class="info-item">
              <div class="info-label">Created</div>
              <div class="info-value">
                {{ provider.created_at ? new Date(provider.created_at).toLocaleDateString() : 'N/A' }}
              </div>
            </div>

            <div class="info-item" v-if="provider.updated_at">
              <div class="info-label">Last Updated</div>
              <div class="info-value">
                {{ new Date(provider.updated_at).toLocaleDateString() }}
              </div>
            </div>
          </div>
        </NCard>
      </template>
    </div>
  </div>

  <!-- Profile Image Upload Modal -->
  <NModal v-model:show="showImageModal" preset="card" title="Upload Profile Picture" :style="{ width: '400px' }">
    <template #header-extra>
      <NButton circle size="small" @click="closeImageModal">
        <NIcon>
          <CloseIcon />
        </NIcon>
      </NButton>
    </template>

    <div class="upload-modal-content">
      <div v-if="imageUploadError" class="upload-error">
        <NAlert type="error" :title="imageUploadError" closable />
      </div>

      <NUpload :custom-request="handleImageUpload" :max="1" accept="image/*" :show-file-list="false">
        <NUploadDragger>
          <div class="upload-dragger-content">
            <NIcon size="48" class="upload-icon">
              <UploadIcon />
            </NIcon>
            <p>Click or drag image to this area to upload</p>
            <p class="upload-hint">
              Support for JPEG, PNG, or GIF, max size 2MB
            </p>
          </div>
        </NUploadDragger>
      </NUpload>

      <div v-if="isUploadingImage" class="upload-loading">
        <NSpin size="medium" />
        <p>Uploading your image...</p>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer">
        <NButton @click="closeImageModal">Cancel</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.provider-dashboard-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.retry-button {
  margin-top: 16px;
}

.profile-header {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: #f9fbff;
  border: 1px solid #eaeffa;
}

.profile-header-content {
  display: flex;
  align-items: center;
  padding: 16px;
}

.profile-avatar-container {
  position: relative;
  margin-right: 24px;
}

.profile-avatar {
  cursor: pointer;
  width: 80px;
  height: 80px;
  font-size: 32px;
  background: linear-gradient(135deg, #2979ff, #1890ff);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
  transition: transform 0.2s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.camera-button {
  position: absolute;
  right: -5px;
  bottom: -5px;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.avatar-popover {
  padding: 8px;
  text-align: center;
}

.avatar-popover p {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin-top: 0;
  margin-bottom: 6px;
  font-size: 1.5rem;
  color: #0e639c;
  font-weight: 600;
}

.provider-type-display {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 1rem;
  color: #666;
  flex-wrap: wrap;
}

.profile-completeness {
  width: 100%;
  max-width: 300px;
  margin-top: 8px;
}

.completeness-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.completeness-header span {
  font-size: 0.9rem;
  color: #516173;
  font-weight: 500;
}

.profile-actions {
  margin-left: 24px;
}

.profile-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #eaeffa;
}

.verification-card {
  border: 1px solid #f0a020;
  background: linear-gradient(135deg, #fff9e6, #ffffff);
}

.verification-content {
  padding: 16px 0;
}

/* NEW STYLES FOR VERIFICATION WORKFLOW */
.verification-link-section {
  margin: 24px 0;
  padding: 20px;
  background-color: #f0f7ff;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
}

.verification-link-section h3 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #0e639c;
  font-size: 1.1rem;
}

.step-description {
  color: #666;
  margin-bottom: 16px;
  line-height: 1.6;
}

.link-hint {
  margin-top: 12px;
  font-size: 0.85rem;
  color: #999;
  font-style: italic;
}

.verification-submit-section {
  margin-top: 24px;
}

.verification-submit-section h3 {
  margin-bottom: 12px;
  color: #0e639c;
  font-size: 1.1rem;
}

.clockclear-input-container {
  margin: 16px 0 20px 0;
}

.confirmation-checkbox {
  margin: 16px 0;
  padding: 12px;
  background-color: #fff9e6;
  border-radius: 8px;
  border-left: 4px solid #f0a020;
}

.submit-button-container {
  margin-bottom: 20px;
}

.info-alert {
  margin-top: 20px;
}

.info-alert ul {
  margin: 8px 0 0 0;
  padding-left: 20px;
}

.info-alert li {
  margin: 6px 0;
  line-height: 1.5;
}

.verification-checklist {
  margin: 20px 0;
}

.verification-checklist h3 {
  margin-bottom: 16px;
  color: #0e639c;
  font-size: 1.1rem;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
}

.verification-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.profile-section :deep(.n-card-header) {
  background-color: #f5f8ff;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0e639c;
  padding: 16px 24px;
}

.profile-section :deep(.n-card__content) {
  padding: 20px 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}

.info-item {
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.info-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.info-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.status-positive {
  color: #18a058;
}

.status-negative {
  color: #d03050;
}

.capitalize {
  text-transform: capitalize;
}

.phone-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phone-number {
  font-family: monospace;
  letter-spacing: 0.5px;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.social-item {
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.social-item a {
  color: #1890ff;
  text-decoration: none;
  transition: color 0.2s ease;
}

.social-item a:hover {
  color: #0070e0;
  text-decoration: underline;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  color: #0e639c;
  margin-top: 24px;
  margin-bottom: 16px;
}

.section-icon {
  margin-right: 8px;
  color: #1890ff;
}

.empty-list-message {
  color: #8c9bab;
  font-style: italic;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  font-size: 0.95rem;
}

.specialty-tag,
.tag-item,
.insurance-tag,
.procedure-tag,
.language-tag,
.payment-tag,
.accessibility-tag {
  margin: 4px;
  padding: 6px 12px;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.specialty-tag:hover,
.tag-item:hover,
.insurance-tag:hover,
.procedure-tag:hover,
.language-tag:hover,
.payment-tag:hover,
.accessibility-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
}

.notes-box {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
  white-space: pre-line;
  line-height: 1.6;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1890ff;
}

.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.location-item {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid #18a058;
  transition: all 0.2s ease;
}

.location-item:hover {
  background-color: #f0f9f5;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.location-address {
  line-height: 1.6;
}

/* Facility images grid */
.facility-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.facility-image-card {
  position: relative;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.facility-image-card:hover {
  transform: scale(1.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.facility-image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 12px;
  font-size: 0.85rem;
}

.image-type {
  font-weight: 600;
  margin-bottom: 4px;
}

.image-description {
  font-size: 0.75rem;
  opacity: 0.9;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.manage-images-button {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

/* Upload modal styling */
.upload-modal-content {
  padding: 16px 0;
}

.upload-dragger-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.upload-icon {
  color: #1890ff;
  margin-bottom: 16px;
}

.upload-hint {
  font-size: 0.85rem;
  color: #999;
  margin-top: 8px;
}

.upload-error {
  margin-bottom: 16px;
}

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .provider-dashboard-container {
    padding: 10px;
  }

  .profile-header-content {
    flex-direction: column;
    text-align: center;
  }

  .profile-avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }

  .profile-actions {
    margin-left: 0;
    margin-top: 15px;
  }

  .profile-completeness {
    max-width: 100%;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .facility-images-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .facility-image-card {
    height: 120px;
  }

  .verification-actions {
    flex-direction: column;
  }

  .provider-type-display {
    justify-content: center;
  }
}

.welcome-alert {
  margin-bottom: 20px;
  border-radius: 12px;
}
</style>